import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Search, Calendar, AlertCircle, CheckCircle, Shield, Key, CreditCard, Package, Info, Zap, Eye, ArrowRight, X, ExternalLink } from 'lucide-react';
import { emailService } from '../api/emailService';

export default function EmailInbox() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // 模拟邮件数据的类型映射
  const emailTypeIcons = {
    verification: Shield,
    payment: CreditCard,
    delivery: Package,
    security: Key,
    default: Mail
  };

  // 从邮件正文中提取预览文本
  const extractPreviewFromBody = (body) => {
    if (!body) return '无内容';
    
    // 移除HTML标签
    const textContent = body.replace(/<[^>]*>/g, '');
    
    // 移除多余的空白字符
    const cleanText = textContent.replace(/\s+/g, ' ').trim();
    
    // 截取前150个字符作为预览
    return cleanText.length > 150 ? cleanText.substring(0, 150) + '...' : cleanText;
  };

  // 检测邮件类型
  const detectEmailType = (subject, body) => {
    const content = (subject + ' ' + body).toLowerCase();
    
    if (content.includes('验证码') || content.includes('verification code') || 
        content.includes('verify') || content.includes('验证') ||
        content.includes('code') || /\b\d{4,8}\b/.test(content)) {
      return 'verification';
    }
    if (content.includes('支付') || content.includes('payment') || 
        content.includes('订单') || content.includes('order')) {
      return 'payment';
    }
    if (content.includes('发货') || content.includes('delivery') || 
        content.includes('shipped') || content.includes('shipping')) {
      return 'delivery';
    }
    if (content.includes('安全') || content.includes('security') || 
        content.includes('login') || content.includes('登录')) {
      return 'security';
    }
    return 'default';
  };


  // 打开邮件详情
  const handleEmailClick = (emailItem) => {
    setSelectedEmail(emailItem);
    setShowEmailModal(true);
  };

  // 关闭邮件详情
  const handleCloseModal = () => {
    setShowEmailModal(false);
    setSelectedEmail(null);
  };

  const getEmailTypeIcon = (subject) => {
    if (subject.toLowerCase().includes('验证') || subject.toLowerCase().includes('verify')) {
      return emailTypeIcons.verification;
    }
    if (subject.toLowerCase().includes('支付') || subject.toLowerCase().includes('payment')) {
      return emailTypeIcons.payment;
    }
    if (subject.toLowerCase().includes('发货') || subject.toLowerCase().includes('delivery')) {
      return emailTypeIcons.delivery;
    }
    if (subject.toLowerCase().includes('安全') || subject.toLowerCase().includes('security')) {
      return emailTypeIcons.security;
    }
    return emailTypeIcons.default;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email) {
      setError(t('emailInbox.errors.emptyEmail'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await emailService.queryRecentEmails(email);
      
      if (result.success) {
        // 处理真实API返回的邮件数据
        const processedEmails = (result.data.emails || []).map(emailItem => ({
          id: emailItem.seq || Math.random().toString(36).substr(2, 9),
          subject: emailItem.subject || '无主题',
          from: emailItem.from || '未知发件人',
          preview: extractPreviewFromBody(emailItem.body || ''),
          date: emailItem.timestamp ? new Date(emailItem.timestamp).toISOString() : new Date(emailItem.date || Date.now()).toISOString(),
          isRead: emailItem.folder !== 'JUNK', // 垃圾邮件文件夹的邮件标记为未读
          type: detectEmailType(emailItem.subject || '', emailItem.body || ''),
          folder: emailItem.folder || 'INBOX',
          body: emailItem.body || ''
        }));
        
        setEmails(processedEmails);
      } else {
        // 处理API错误
        setError(result.message || t('emailInbox.errors.queryFailed'));
      }
    } catch (err) {
      setError(t('emailInbox.errors.queryFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return t('emailInbox.emailList.time.minutesAgo', `${diffMins}分钟前`, { count: diffMins });
    } else if (diffHours < 24) {
      return t('emailInbox.emailList.time.hoursAgo', `${diffHours}小时前`, { count: diffHours });
    } else if (diffDays < 7) {
      return t('emailInbox.emailList.time.daysAgo', `${diffDays}天前`, { count: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 - 使用 Sunset Glow Card System 风格 */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 via-red-400/20 to-amber-400/20"></div>
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl shadow-lg mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('emailInbox.pageTitle')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('emailInbox.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>


      {/* 使用指南 */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('emailInbox.guide.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">1</span>
              </div>
              <p className="text-sm text-gray-700">{t('emailInbox.guide.steps.step1')}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">2</span>
              </div>
              <p className="text-sm text-gray-700">{t('emailInbox.guide.steps.step2')}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">3</span>
              </div>
              <p className="text-sm text-gray-700">{t('emailInbox.guide.steps.step3')}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">4</span>
              </div>
              <p className="text-sm text-gray-700">{t('emailInbox.guide.steps.step4')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 搜索区域 */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-2xl shadow-sm p-6">
          {/* 验证码获取提示 */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-orange-900 mb-1">
                  {t('emailInbox.quickTip.title', '快速获取验证码')}
                </h3>
                <p className="text-sm text-orange-700">
                  {t('emailInbox.quickTip.description', '输入您的邮箱地址，即可查看最近收到的验证码邮件，包括注册验证、登录验证、找回密码等各类验证码信息。')}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSearch} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors duration-300"
                placeholder={t('emailInbox.searchPlaceholder')}
              />
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-800 mb-1">
                      {t('emailInbox.errors.title', '查询失败')}
                    </h4>
                    <p className="text-sm text-red-700">{error}</p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setError('')}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        {t('emailInbox.errors.dismiss', '关闭')}
                      </button>
                      <button
                        onClick={handleSearch}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        {t('emailInbox.retry', '重试')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-4 rounded-xl font-semibold hover:from-orange-600 hover:to-amber-600 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span className="ml-2">{t('emailInbox.loading')}</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  {t('emailInbox.searchButton')}
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* 邮件列表 */}
      {emails.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('emailInbox.emailList.title', '最近邮件')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('emailInbox.emailList.foundEmails', `找到 ${emails.length} 封邮件`, { count: emails.length })}
              </p>
            </div>
            
            <div className="divide-y divide-gray-100">
              {emails.map((emailItem) => {
                const IconComponent = getEmailTypeIcon(emailItem.subject);
                return (
                  <div
                    key={emailItem.id}
                    className={`px-6 py-3 hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${
                      !emailItem.isRead ? 'bg-orange-50/30' : ''
                    }`}
                    onClick={() => handleEmailClick(emailItem)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        !emailItem.isRead 
                          ? 'bg-gradient-to-br from-orange-500 to-amber-500' 
                          : 'bg-gray-100'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${
                          !emailItem.isRead ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h3 className={`text-sm font-medium ${
                              !emailItem.isRead ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {emailItem.subject}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {emailItem.from}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(emailItem.date)}
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </div>
                        </div>
                        {!emailItem.isRead && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            {t('emailInbox.status.unread', '未读')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 空状态 */}
      {!loading && emails.length === 0 && email && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Mail className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('emailInbox.noResults')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('emailInbox.status.noEmailsFound')}
            </p>
            <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-blue-700">
                💡 {t('emailInbox.emptyTip', '提示：请确保邮箱地址正确，或稍后再试。验证码邮件通常在发送后几分钟内到达。')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 首次访问引导 */}
      {!loading && emails.length === 0 && !email && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mb-6">
              <Mail className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {t('emailInbox.welcome.title', '开始查询您的验证码邮件')}
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              {t('emailInbox.welcome.description', '在上方输入您的邮箱地址，我们将为您查找最近收到的验证码等重要邮件。')}
            </p>
          </div>
        </section>
      )}

      {/* 邮件详情弹窗 */}
      {showEmailModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  !selectedEmail.isRead 
                    ? 'bg-gradient-to-br from-orange-500 to-amber-500' 
                    : 'bg-gray-100'
                }`}>
                  <Mail className={`w-4 h-4 ${
                    !selectedEmail.isRead ? 'text-white' : 'text-gray-600'
                  }`} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 line-clamp-2">
                    {selectedEmail.subject}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {selectedEmail.from}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* 邮件元信息 */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">
                    <strong>发件人:</strong> {selectedEmail.from}
                  </span>
                  <span className="text-gray-600">
                    <strong>时间:</strong> {formatDate(selectedEmail.date)}
                  </span>
                </div>
                {selectedEmail.folder && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedEmail.folder === 'JUNK' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {selectedEmail.folder}
                  </span>
                )}
              </div>
            </div>

            {/* 邮件内容 */}
            <div className="p-6 overflow-auto max-h-[60vh]">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
              />
            </div>

            {/* 弹窗底部 */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  关闭
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedEmail.body.replace(/<[^>]*>/g, ''));
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    复制内容
                  </button>
                  <button
                    onClick={() => {
                      const newWindow = window.open('', '_blank');
                      newWindow.document.write(`
                        <html>
                          <head>
                            <title>${selectedEmail.subject}</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 20px; }
                              .header { border-bottom: 1px solid #ccc; padding-bottom: 10px; margin-bottom: 20px; }
                            </style>
                          </head>
                          <body>
                            <div class="header">
                              <h1>${selectedEmail.subject}</h1>
                              <p><strong>发件人:</strong> ${selectedEmail.from}</p>
                              <p><strong>时间:</strong> ${new Date(selectedEmail.date).toLocaleString()}</p>
                            </div>
                            ${selectedEmail.body}
                          </body>
                        </html>
                      `);
                      newWindow.document.close();
                    }}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    新窗口打开
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}